import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import SearchIcon from '@mui/icons-material/Search';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAuth0 } from '@auth0/auth0-react';
import CreatePromptModal from './CreatePromptModal';
import { API_BASE } from '../../shared/Constants';
import { useFeatureFlags } from '../../shared/context/FeatureFlagsContext';
// Category icons
import SchoolIcon from '@mui/icons-material/School';
import GavelIcon from '@mui/icons-material/Gavel';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import CampaignIcon from '@mui/icons-material/Campaign';
import CodeIcon from '@mui/icons-material/Code';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
// Prompt icons
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import QuizIcon from '@mui/icons-material/Quiz';
import SummarizeIcon from '@mui/icons-material/Summarize';
import RateReviewIcon from '@mui/icons-material/RateReview';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import ChecklistIcon from '@mui/icons-material/Checklist';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import DescriptionIcon from '@mui/icons-material/Description';
import BalanceIcon from '@mui/icons-material/Balance';
import EditNoteIcon from '@mui/icons-material/EditNote';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BiotechIcon from '@mui/icons-material/Biotech';
import ScienceIcon from '@mui/icons-material/Science';
import MedicationIcon from '@mui/icons-material/Medication';
import ArticleIcon from '@mui/icons-material/Article';
import CompareIcon from '@mui/icons-material/Compare';
import CreateIcon from '@mui/icons-material/Create';
import EmailIcon from '@mui/icons-material/Email';
import GroupsIcon from '@mui/icons-material/Groups';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import PersonIcon from '@mui/icons-material/Person';
import WebIcon from '@mui/icons-material/Web';
import PaidIcon from '@mui/icons-material/Paid';
import BugReportIcon from '@mui/icons-material/BugReport';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import StorageIcon from '@mui/icons-material/Storage';
import ApiIcon from '@mui/icons-material/Api';
import TransformIcon from '@mui/icons-material/Transform';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import SecurityIcon from '@mui/icons-material/Security';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import GridViewIcon from '@mui/icons-material/GridView';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EventNoteIcon from '@mui/icons-material/EventNote';
import WorkIcon from '@mui/icons-material/Work';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
// Engineering icons
import EngineeringIcon from '@mui/icons-material/Engineering';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import StraightenIcon from '@mui/icons-material/Straighten';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import WaterIcon from '@mui/icons-material/Water';
import SolarPowerIcon from '@mui/icons-material/SolarPower';
import BlenderIcon from '@mui/icons-material/Blender';
import CycloneIcon from '@mui/icons-material/Cyclone';
// Wellness icons
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
// Location / Data Ocean icons
import StorefrontIcon from '@mui/icons-material/Storefront';
import StoreIcon from '@mui/icons-material/Store';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PlaceIcon from '@mui/icons-material/Place';
import StoreMallDirectoryIcon from '@mui/icons-material/StoreMallDirectory';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import ShieldIcon from '@mui/icons-material/Shield';
import HomeIcon from '@mui/icons-material/Home';
// Category icons (agriculture, tourism)
import GrassIcon from '@mui/icons-material/Grass';
import FlightIcon from '@mui/icons-material/Flight';
// L2 composite prompt icons
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import MemoryIcon from '@mui/icons-material/Memory';
import NaturePeopleIcon from '@mui/icons-material/NaturePeople';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import RadarIcon from '@mui/icons-material/Radar';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import RiceBowlIcon from '@mui/icons-material/RiceBowl';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import WarningIcon from '@mui/icons-material/Warning';
// Additional prompt icons (data ocean categories)
import AccessibleIcon from '@mui/icons-material/Accessible';
import AirplanemodeActiveIcon from '@mui/icons-material/AirplanemodeActive';
import BarChartIcon from '@mui/icons-material/BarChart';
import CoronavirusIcon from '@mui/icons-material/Coronavirus';
import DateRangeIcon from '@mui/icons-material/DateRange';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HealingIcon from '@mui/icons-material/Healing';
import HotelIcon from '@mui/icons-material/Hotel';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LuggageIcon from '@mui/icons-material/Luggage';
import MapIcon from '@mui/icons-material/Map';
import PeopleIcon from '@mui/icons-material/People';
import PetsIcon from '@mui/icons-material/Pets';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import PregnantWomanIcon from '@mui/icons-material/PregnantWoman';

import { promptCategories, Category, Prompt, displayGroups, getPromptGroup } from './promptsData';

// Map icon names to components
const iconMap: Record<string, React.ReactNode> = {
  // Category icons
  School: <SchoolIcon />,
  Gavel: <GavelIcon />,
  LocalHospital: <LocalHospitalIcon />,
  Campaign: <CampaignIcon />,
  Code: <CodeIcon />,
  BusinessCenter: <BusinessCenterIcon />,
  FolderSpecial: <FolderSpecialIcon />,
  // Prompt icons
  MenuBook: <MenuBookIcon />,
  Lightbulb: <LightbulbIcon />,
  Quiz: <QuizIcon />,
  Summarize: <SummarizeIcon />,
  RateReview: <RateReviewIcon />,
  AutoStories: <AutoStoriesIcon />,
  Checklist: <ChecklistIcon />,
  Diversity3: <Diversity3Icon />,
  FindInPage: <FindInPageIcon />,
  Description: <DescriptionIcon />,
  Balance: <BalanceIcon />,
  EditNote: <EditNoteIcon />,
  FactCheck: <FactCheckIcon />,
  Search: <SearchIcon />,
  VerifiedUser: <VerifiedUserIcon />,
  PersonSearch: <PersonSearchIcon />,
  Assignment: <AssignmentIcon />,
  Biotech: <BiotechIcon />,
  Science: <ScienceIcon />,
  Medication: <MedicationIcon />,
  Article: <ArticleIcon />,
  Compare: <CompareIcon />,
  Create: <CreateIcon />,
  Email: <EmailIcon />,
  Groups: <GroupsIcon />,
  CalendarMonth: <CalendarMonthIcon />,
  Analytics: <AnalyticsIcon />,
  Person: <PersonIcon />,
  Web: <WebIcon />,
  Paid: <PaidIcon />,
  BugReport: <BugReportIcon />,
  AccountTree: <AccountTreeIcon />,
  Storage: <StorageIcon />,
  Api: <ApiIcon />,
  Transform: <TransformIcon />,
  TextFields: <TextFieldsIcon />,
  Security: <SecurityIcon />,
  PlaylistAddCheck: <PlaylistAddCheckIcon />,
  GridView: <GridViewIcon />,
  Slideshow: <SlideshowIcon />,
  TrackChanges: <TrackChangesIcon />,
  AttachMoney: <AttachMoneyIcon />,
  EventNote: <EventNoteIcon />,
  Work: <WorkIcon />,
  RequestQuote: <RequestQuoteIcon />,
  // Engineering icons
  Engineering: <EngineeringIcon />,
  AcUnit: <AcUnitIcon />,
  Straighten: <StraightenIcon />,
  ElectricalServices: <ElectricalServicesIcon />,
  ViewInAr: <ViewInArIcon />,
  Water: <WaterIcon />,
  SolarPower: <SolarPowerIcon />,
  Blender: <BlenderIcon />,
  Cyclone: <CycloneIcon />,
  // Wellness icons
  FavoriteBorder: <FavoriteBorderIcon />,
  HelpOutline: <HelpOutlineIcon />,
  Bedtime: <BedtimeIcon />,
  SelfImprovement: <SelfImprovementIcon />,
  Restaurant: <RestaurantIcon />,
  FitnessCenter: <FitnessCenterIcon />,
  // Location / Data Ocean icons
  Storefront: <StorefrontIcon />,
  Store: <StoreIcon />,
  TrendingUp: <TrendingUpIcon />,
  Place: <PlaceIcon />,
  StoreMallDirectory: <StoreMallDirectoryIcon />,
  CompareArrows: <CompareArrowsIcon />,
  SearchOff: <SearchOffIcon />,
  Shield: <ShieldIcon />,
  Home: <HomeIcon />,
  // Category icons
  Grass: <GrassIcon />,
  Flight: <FlightIcon />,
  // L2 composite prompt icons
  AccountBalance: <AccountBalanceIcon />,
  AddLocation: <AddLocationIcon />,
  AdminPanelSettings: <AdminPanelSettingsIcon />,
  Agriculture: <AgricultureIcon />,
  AreaChart: <ShowChartIcon />,
  Assessment: <AssessmentIcon />,
  FamilyRestroom: <FamilyRestroomIcon />,
  FlightTakeoff: <FlightTakeoffIcon />,
  LocalPharmacy: <LocalPharmacyIcon />,
  Memory: <MemoryIcon />,
  NaturePeople: <NaturePeopleIcon />,
  PeopleAlt: <PeopleAltIcon />,
  Radar: <RadarIcon />,
  ReportProblem: <ReportProblemIcon />,
  RiceBowl: <RiceBowlIcon />,
  ScatterPlot: <ScatterPlotIcon />,
  ShowChart: <ShowChartIcon />,
  Warning: <WarningIcon />,
  // Additional prompt icons (data ocean categories)
  Accessible: <AccessibleIcon />,
  AirplanemodeActive: <AirplanemodeActiveIcon />,
  BarChart: <BarChartIcon />,
  Coronavirus: <CoronavirusIcon />,
  DateRange: <DateRangeIcon />,
  EmojiEvents: <EmojiEventsIcon />,
  Healing: <HealingIcon />,
  Hotel: <HotelIcon />,
  Inventory: <InventoryIcon />,
  LocationOn: <LocationOnIcon />,
  Luggage: <LuggageIcon />,
  Map: <MapIcon />,
  People: <PeopleIcon />,
  Pets: <PetsIcon />,
  Timeline: <TimelineIcon />,
  TrendingDown: <TrendingDownIcon />,
  StoreFront: <StorefrontIcon />,  // alias for capitalization variant
  ChildCare: <ChildCareIcon />,
  MonitorWeight: <MonitorWeightIcon />,
  PregnantWoman: <PregnantWomanIcon />,
};

interface PromptBankModalProps {
  open: boolean;
  onClose: () => void;
  onSelectPrompt: (prompt: Prompt) => void;
  initialCategoryId?: string;
  onPromptsChange?: () => void;
  modelName?: string;
}

// Models that have access to Data Ocean tools (BigQuery, APIs, etc.)
const DATA_OCEAN_MODELS = new Set(["sabia-5", "sabia-4", "sabia-4-thinking", "sabiazinho-4", "competidor", "competidor-2", "competidor-3"]);


const VCARD_H = 158;      // fixed card height (px) for row virtualization
const VROW_GAP = 14;
const VROW_H = VCARD_H + VROW_GAP;
const VOVERSCAN = 4;      // rows rendered above/below the viewport

const PromptBankModal: React.FC<PromptBankModalProps> = ({
  open,
  onClose,
  onSelectPrompt,
  initialCategoryId,
  onPromptsChange,
  modelName,
}) => {
  const { data_ocean_tools } = useFeatureFlags();
  const hasToolAccess = data_ocean_tools && DATA_OCEAN_MODELS.has(modelName || '');
  const useSimpleCategories = !data_ocean_tools;
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('all');
  const [customPrompts, setCustomPrompts] = useState<Prompt[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [customPromptsLoaded, setCustomPromptsLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hoveredPrompt, setHoveredPrompt] = useState<Prompt | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [listScrollTop, setListScrollTop] = useState(0);
  const [listViewportH, setListViewportH] = useState(560);
  const [listCols, setListCols] = useState(2);

  // Reset the virtual scroll when the filter changes.
  useEffect(() => {
    setListScrollTop(0);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [searchQuery, selectedCategoryId, open]);

  // Measure the scroll viewport (height + how many columns fit).
  useEffect(() => {
    if (!open) return;
    const measure = () => {
      const el = listRef.current;
      if (el) {
        setListViewportH(el.clientHeight);
        setListCols(el.clientWidth < 640 ? 1 : 2);
      }
    };
    const id = window.setTimeout(measure, 0);
    window.addEventListener('resize', measure);
    return () => { window.clearTimeout(id); window.removeEventListener('resize', measure); };
  }, [open]);
  const tabsContainerRef = useRef<HTMLDivElement>(null);

  // Check scroll position to show/hide arrows
  const checkScrollPosition = useCallback(() => {
    const container = tabsContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 1
      );
    }
  }, []);

  // Scroll tabs left or right
  const scrollTabs = (direction: 'left' | 'right') => {
    const container = tabsContainerRef.current;
    if (container) {
      const scrollAmount = 200;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  // Check scroll on mount and when tabs change
  useEffect(() => {
    checkScrollPosition();
    const container = tabsContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
      // Also check on resize
      window.addEventListener('resize', checkScrollPosition);
      return () => {
        container.removeEventListener('scroll', checkScrollPosition);
        window.removeEventListener('resize', checkScrollPosition);
      };
    }
  }, [checkScrollPosition, open]);

  // Load custom prompts from API
  const loadCustomPrompts = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setIsLoading(true);
      setCustomPromptsLoaded(false);
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE}/api/prompts/list`, {
        method: 'GET',
        headers: {
          Authorization: `Auth ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load custom prompts: ${response.status}`);
      }
      const data = await response.json();
      if (!Array.isArray(data.prompts)) {
        throw new Error('Failed to load custom prompts: invalid response');
      }
      setCustomPrompts(data.prompts);
      setCustomPromptsLoaded(true);
    } catch (error) {
      console.error('Failed to load custom prompts:', error);
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  const mutateCustomPrompt = useCallback(async (
    promptId: string,
    method: 'PUT' | 'DELETE',
    prompt?: Prompt,
  ): Promise<Prompt[]> => {
    if (!isAuthenticated) throw new Error('Not authenticated');
    const token = await getAccessTokenSilently();
    const response = await fetch(`${API_BASE}/api/prompts/${encodeURIComponent(promptId)}`, {
      method,
      headers: {
        Authorization: `Auth ${token}`,
        ...(prompt ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(prompt ? { body: JSON.stringify(prompt) } : {}),
    });
    if (!response.ok) throw new Error(`Failed to mutate custom prompt: ${response.status}`);
    const data = await response.json();
    if (!Array.isArray(data.prompts)) throw new Error('Invalid custom prompts response');
    return data.prompts;
  }, [getAccessTokenSilently, isAuthenticated]);

  // Load custom prompts when modal opens
  useEffect(() => {
    if (open && isAuthenticated) {
      loadCustomPrompts();
    }
  }, [open, isAuthenticated, loadCustomPrompts]);

  // Handle initial category when modal opens
  useEffect(() => {
    if (open && initialCategoryId) {
      setSelectedCategoryId(initialCategoryId);
    }
  }, [open, initialCategoryId]);

  // Reset search when modal closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open]);

  // Get all prompts (custom + predefined) with filtering
  const filteredPrompts = useMemo(() => {
    // Get all predefined prompts from categories
    const allPredefinedPrompts: (Prompt & { categoryId: string; categoryName: string })[] = [];
    promptCategories.forEach(category => {
      category.prompts.forEach(prompt => {
        allPredefinedPrompts.push({
          ...prompt,
          categoryId: category.id,
          categoryName: category.name,
        });
      });
    });

    // Add custom prompts with special category
    const customWithCategory = customPrompts.map(p => ({
      ...p,
      categoryId: 'my-prompts',
      categoryName: 'Meus Prompts',
    }));

    let allPrompts = [...customWithCategory, ...allPredefinedPrompts];

    // Filter out tool-calling prompts for models without Data Ocean access.
    // Identified by requiresTools (tag explícita) ou, nos legados, pela
    // presença de systemPrefix.
    if (!hasToolAccess) {
      allPrompts = allPrompts.filter(p => !p.systemPrefix && !p.requiresTools);
    }

    // Filter by display group or category (skip if searching - search across all)
    if (!searchQuery.trim()) {
      if (selectedCategoryId === 'featured') {
        allPrompts = allPrompts.filter(p => p.featured);
      } else if (selectedCategoryId === 'my-prompts') {
        allPrompts = allPrompts.filter(p => p.categoryId === 'my-prompts');
      } else if (selectedCategoryId !== 'all') {
        if (useSimpleCategories) {
          // Simple mode: filter by category ID directly
          allPrompts = allPrompts.filter(p => p.categoryId === selectedCategoryId);
        } else {
          // Data Ocean mode: filter by display group
          allPrompts = allPrompts.filter(p => getPromptGroup(p.id, p.categoryId) === selectedCategoryId);
        }
      }
    }

    // Deduplicate by prompt ID (some prompts appear in multiple categories)
    const seen = new Set<string>();
    allPrompts = allPrompts.filter(p => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });

    // Filter by search query (case + accent insensitive)
    if (searchQuery.trim()) {
      const normalize = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      // Token-AND over title/description/category. The template body is NOT
      // searched: templates are long prose, so any common word would match
      // nearly every prompt and the search would never narrow.
      const tokens = normalize(searchQuery).split(/\s+/).filter(Boolean);
      allPrompts = allPrompts.filter(p => {
        const hay = normalize(`${p.title} ${p.description || ''} ${p.categoryName || ''}`);
        return tokens.every(t => hay.includes(t));
      });
    }

    // Featured prompts are shown in the Destaques tab, no need to sort here

    return allPrompts;
  }, [customPrompts, selectedCategoryId, searchQuery, hasToolAccess, useSimpleCategories]);

  // Original categories from main branch (shown when Data Ocean is disabled)
  const MAIN_BRANCH_CATEGORIES = useMemo(() => new Set(['education', 'legal', 'health', 'wellness', 'marketing', 'tech', 'business', 'engineering']), []);

  // Simple categories for non-Data-Ocean mode
  const simpleCategories = useMemo(() => {
    if (!useSimpleCategories) return [];
    return promptCategories.filter(c => MAIN_BRANCH_CATEGORIES.has(c.id));
  }, [useSimpleCategories, MAIN_BRANCH_CATEGORIES]);

  // Filter display groups that have visible prompts (hide empty groups for non-pro models)
  const visibleGroups = useMemo(() => {
    if (useSimpleCategories) return []; // not used in simple mode
    if (hasToolAccess) return displayGroups;
    // Compute which groups have at least one non-systemPrefix prompt
    const groupsWithPrompts = new Set<string>();
    promptCategories.forEach(category => {
      category.prompts.forEach(prompt => {
        if (!prompt.systemPrefix) {
          groupsWithPrompts.add(getPromptGroup(prompt.id, category.id));
        }
      });
    });
    return displayGroups.filter(g => groupsWithPrompts.has(g.id));
  }, [hasToolAccess, useSimpleCategories]);

  const canMutateCustomPrompts = customPromptsLoaded && !isLoading;

  const handleOpenCreatePrompt = () => {
    if (!canMutateCustomPrompts) return;
    setShowCreateModal(true);
  };

  const handleSaveCustomPrompt = async (prompt: Prompt) => {
    if (!canMutateCustomPrompts) return;
    const previous = customPrompts;
    const existingIndex = customPrompts.findIndex((p) => p.id === prompt.id);
    let updated;
    if (existingIndex >= 0) {
      updated = [...customPrompts];
      updated[existingIndex] = prompt;
    } else {
      updated = [...customPrompts, prompt];
    }
    setCustomPrompts(updated);
    try {
      setCustomPrompts(await mutateCustomPrompt(prompt.id, 'PUT', prompt));
      setEditingPrompt(null);
      onPromptsChange?.();
    } catch (error) {
      setCustomPrompts(previous);
      console.error('Failed to save custom prompt:', error);
    }
  };

  const handleDeleteCustomPrompt = async (promptId: string, e: React.MouseEvent) => {
    if (!canMutateCustomPrompts) return;
    e.stopPropagation();
    // eslint-disable-next-line no-restricted-globals
    if (window.confirm('Tem certeza que deseja excluir este prompt?')) {
      const previous = customPrompts;
      const updated = customPrompts.filter((p) => p.id !== promptId);
      setCustomPrompts(updated);
      try {
        setCustomPrompts(await mutateCustomPrompt(promptId, 'DELETE'));
        onPromptsChange?.();
      } catch (error) {
        setCustomPrompts(previous);
        console.error('Failed to delete custom prompt:', error);
      }
    }
  };

  const handleEditCustomPrompt = (prompt: Prompt, e: React.MouseEvent) => {
    if (!canMutateCustomPrompts) return;
    e.stopPropagation();
    setEditingPrompt(prompt);
    setShowCreateModal(true);
  };

  const handlePromptClick = (prompt: Prompt) => {
    onSelectPrompt(prompt);
    setSelectedCategoryId('all');
    setSearchQuery('');
    setHoveredPrompt(null);
  };

  // Render template with highlighted variables
  const renderTemplatePreview = (prompt: Prompt) => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    const regex = /\{\{(\w+)\}\}/g;
    let match;

    while ((match = regex.exec(prompt.template)) !== null) {
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`}>
            {prompt.template.slice(lastIndex, match.index)}
          </span>
        );
      }

      const varKey = match[1];
      const variable = prompt.variables.find((v) => v.key === varKey);
      parts.push(
        <HighlightedVar key={`var-${match.index}`}>
          {variable?.label || varKey}
        </HighlightedVar>
      );

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < prompt.template.length) {
      parts.push(
        <span key={`text-${lastIndex}`}>
          {prompt.template.slice(lastIndex)}
        </span>
      );
    }

    return parts;
  };

  const handleClose = () => {
    onClose();
    // Reset state for next open
    setSelectedCategoryId('all');
    setSearchQuery('');
  };

  const renderPromptCard = (prompt: Prompt) => {
    const isCustom = prompt.categoryId === 'my-prompts';
    return (
      <PromptCard
        key={prompt.id}
        style={{ flex: '1 1 0', minWidth: 0 }}
        onClick={() => handlePromptClick(prompt)}
        onMouseEnter={() => setHoveredPrompt(prompt)}
        onMouseLeave={() => setHoveredPrompt(null)}
        $isHovered={hoveredPrompt?.id === prompt.id}
      >
        <PromptCardHeader>
          <PromptIcon>{(prompt.icon && iconMap[prompt.icon]) || <AutoAwesomeIcon />}</PromptIcon>
          <PromptTitle>{prompt.title.replace('★ ', '')}</PromptTitle>
        </PromptCardHeader>
        {prompt.description && (
          <PromptDescription>{prompt.description}</PromptDescription>
        )}
        <PromptCardFooter>
          {isCustom && (
            <PromptActions>
              <SmallActionButton onClick={(e) => handleEditCustomPrompt(prompt, e)} aria-label="Editar">
                Editar
              </SmallActionButton>
              <SmallActionButton onClick={(e) => handleDeleteCustomPrompt(prompt.id, e)} aria-label="Excluir" $danger>
                Apagar
              </SmallActionButton>
            </PromptActions>
          )}
          {!isCustom && <CategoryBadge>{prompt.categoryName}</CategoryBadge>}
        </PromptCardFooter>
      </PromptCard>
    );
  };

  if (!open) return null;

  return createPortal(
    <>
      <ModalOverlay onClick={handleClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <HeaderLeft>
              <ModalTitle>Biblioteca de prompts <span style={{fontSize: '0.8rem', fontWeight: 400, opacity: 0.6}}>({filteredPrompts.length})</span></ModalTitle>
            </HeaderLeft>
            <HeaderRight>
              <CreateButton onClick={handleOpenCreatePrompt} disabled={!canMutateCustomPrompts}>
                <AddIcon />
                <span>Criar</span>
              </CreateButton>
              <CloseButton onClick={handleClose} aria-label="Fechar">
                <CloseIcon />
              </CloseButton>
            </HeaderRight>
          </ModalHeader>

          {/* Search Bar */}
          <SearchSection>
            <SearchInputWrapper>
              <SearchIcon />
              <SearchInput
                type="text"
                placeholder="Buscar modelos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              {searchQuery && (
                <ClearSearchButton onClick={() => setSearchQuery('')}>
                  <CloseIcon />
                </ClearSearchButton>
              )}
            </SearchInputWrapper>
          </SearchSection>

          {/* Category Tabs */}
          <CategoryTabsWrapper>
            {canScrollLeft && (
              <TabScrollButton $direction="left" onClick={() => scrollTabs('left')}>
                <ChevronLeftIcon />
              </TabScrollButton>
            )}
            <CategoryTabs ref={tabsContainerRef}>
              <CategoryTab
                $active={selectedCategoryId === 'my-prompts'}
                onClick={() => setSelectedCategoryId('my-prompts')}
               
               
              >
                <FolderSpecialIcon />
                Meus ({customPrompts.length})
              </CategoryTab>

              {/* Destaques (only in Data Ocean mode) */}
              {!useSimpleCategories && (
                <CategoryTab
                  $active={selectedCategoryId === 'featured'}
                  onClick={() => setSelectedCategoryId('featured')}
                 
                 
                >
                  ⭐ Destaques
                </CategoryTab>
              )}
              {/* Todos */}
              <CategoryTab
                $active={selectedCategoryId === 'all'}
                onClick={() => setSelectedCategoryId('all')}
               
               
              >
                Todos
              </CategoryTab>
              {/* Simple categories (when Data Ocean is off) */}
              {useSimpleCategories && simpleCategories.map((category) => (
                <CategoryTab
                  key={category.id}
                  $active={selectedCategoryId === category.id}
                  onClick={() => setSelectedCategoryId(category.id)}
                 
                 
                >
                  {iconMap[category.icon]}
                  {category.name}
                </CategoryTab>
              ))}
              {/* Theme groups (Data Ocean mode, skip featured since rendered above) */}
              {!useSimpleCategories && visibleGroups.filter(g => g.id !== 'featured').map((group) => (
                <CategoryTab
                  key={group.id}
                  $active={selectedCategoryId === group.id}
                  onClick={() => setSelectedCategoryId(group.id)}
                 
                 
                >
                  {iconMap[group.icon]}
                  {group.name}
                </CategoryTab>
              ))}

            </CategoryTabs>
            {canScrollRight && (
              <TabScrollButton $direction="right" onClick={() => scrollTabs('right')}>
                <ChevronRightIcon />
              </TabScrollButton>
            )}
          </CategoryTabsWrapper>

          <ModalBody>
            {isLoading ? (
              <LoadingState>Carregando...</LoadingState>
            ) : filteredPrompts.length === 0 ? (
              <EmptyState>
                <EmptyStateIcon>
                  {selectedCategoryId === 'my-prompts' ? <FolderSpecialIcon /> : <SearchIcon />}
                </EmptyStateIcon>
                <EmptyStateTitle>
                  {selectedCategoryId === 'my-prompts' && !searchQuery
                    ? 'Nenhum modelo criado'
                    : 'Nenhum modelo encontrado'}
                </EmptyStateTitle>
                <EmptyStateText>
                  {selectedCategoryId === 'my-prompts' && !searchQuery
                    ? 'Crie seu primeiro modelo personalizado'
                    : 'Tente buscar por outro termo ou categoria'}
                </EmptyStateText>
                {selectedCategoryId === 'my-prompts' && !searchQuery && (
                  <CreateButtonLarge onClick={handleOpenCreatePrompt} disabled={!canMutateCustomPrompts}>
                    <AddIcon />
                    <span>Criar Modelo</span>
                  </CreateButtonLarge>
                )}
              </EmptyState>
            ) : (
              <BodyContent>
                <PromptsList
                  ref={listRef}
                  onScroll={(e) => setListScrollTop((e.currentTarget as HTMLDivElement).scrollTop)}
                >
                  {(() => {
                    const cols = listCols;
                    const totalRows = Math.ceil(filteredPrompts.length / cols);
                    const startRow = Math.max(0, Math.floor(listScrollTop / VROW_H) - VOVERSCAN);
                    const endRow = Math.min(totalRows, Math.ceil((listScrollTop + listViewportH) / VROW_H) + VOVERSCAN);
                    const topPad = startRow * VROW_H;
                    const bottomPad = Math.max(0, (totalRows - endRow) * VROW_H);
                    const rows = [];
                    for (let r = startRow; r < endRow; r++) {
                      const rowItems = filteredPrompts.slice(r * cols, r * cols + cols);
                      rows.push(
                        <VRow key={r}>
                          {rowItems.map((prompt) => renderPromptCard(prompt))}
                          {rowItems.length < cols &&
                            Array.from({ length: cols - rowItems.length }).map((_, i) => (
                              <div key={`sp-${i}`} style={{ flex: 1 }} />
                            ))}
                        </VRow>
                      );
                    }
                    return (
                      <>
                        <div style={{ height: topPad }} />
                        {rows}
                        <div style={{ height: bottomPad }} />
                      </>
                    );
                  })()}
                </PromptsList>
                <PreviewPanel>
                    {hoveredPrompt ? (
                      <>
                        <PreviewHeader>
                          <PreviewTitle>{hoveredPrompt.title}</PreviewTitle>
                          {hoveredPrompt.description && (
                            <PreviewSubtitle>{hoveredPrompt.description}</PreviewSubtitle>
                          )}
                        </PreviewHeader>
                        <PreviewContent>
                          {renderTemplatePreview(hoveredPrompt)}
                        </PreviewContent>
                      </>
                    ) : (
                      <PreviewEmpty>
                        <SearchIcon />
                        <span>Passe o mouse sobre um modelo para ver a prévia do prompt</span>
                      </PreviewEmpty>
                    )}
                </PreviewPanel>
              </BodyContent>
            )}
          </ModalBody>
        </ModalContent>
      </ModalOverlay>

      <CreatePromptModal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingPrompt(null);
        }}
        onSave={handleSaveCustomPrompt}
        editingPrompt={editingPrompt}
      />
    </>,
    document.body
  );
};

export default PromptBankModal;

// ============ Styled Components ============

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: ${({ theme }) => theme.modal_overlay};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const ModalContent = styled.div`
  font-family: var(--font-secondary);
  background: ${({ theme }) => theme.modal_background};
  color: ${({ theme }) => theme.font_color};
  border: 1px solid ${({ theme }) => theme.modal_border};
  border-radius: 20px;
  max-width: 1280px;
  width: min(96vw, 1280px);
  height: 80vh;
  max-height: 820px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.modal_border};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CreateButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  border: none;
  border-radius: 20px;
  background: ${({ theme }) => theme.prompt_accent_color};
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.88;
  }

  & svg {
    font-size: 1rem;
  }
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.text_secondary};
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.hover_overlay_light};
    color: ${({ theme }) => theme.font_color};
  }
`;

const SearchSection = styled.div`
  padding: 10px 24px;
`;

const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.prompt_variable_bg};
  border: 1px solid transparent;
  border-radius: 12px;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:focus-within {
    border-color: ${({ theme }) => theme.modal_border};
  }

  & > svg:first-child {
    font-size: 1.15rem;
    color: ${({ theme }) => theme.text_secondary};
  }
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.font_color};
  outline: none;

  &::placeholder {
    color: ${({ theme }) => theme.text_secondary};
  }
`;

const ClearSearchButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.text_secondary};
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    color: ${({ theme }) => theme.font_color};
  }

  & svg {
    font-size: 0.9rem;
  }
`;

const CategoryTabsWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 0 20px;
  border-bottom: 1px solid ${({ theme }) => theme.modal_border};
`;

const TabScrollButton = styled.button<{ $direction: 'left' | 'right' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 100%;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.text_secondary};
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.font_color};
  }

  & svg {
    font-size: 1.2rem;
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 0;
  flex: 1;
`;

const CategoryTab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 15px;
  border: none;
  border-radius: 999px;
  background: ${({ theme, $active }) =>
    $active ? theme.prompt_accent_color : 'transparent'};
  color: ${({ theme, $active }) =>
    $active ? '#ffffff' : theme.text_secondary};
  font-size: 0.88rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme, $active }) =>
      $active ? theme.prompt_accent_color : theme.hover_overlay_light};
    color: ${({ theme, $active }) => ($active ? '#ffffff' : theme.font_color)};
  }

  & svg {
    font-size: 1rem;
  }
`;

const ModalBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const BodyContent = styled.div`
  display: flex;
  height: 100%;
`;

const PromptsList = styled.div`
  flex: 1;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 18px 22px;
  display: block;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.sidebar_scrollbar};
    border-radius: 3px;
  }
`;

const VRow = styled.div`
  display: flex;
  gap: 14px;
  margin-bottom: 14px;
  height: ${VCARD_H}px;
`;

const PreviewPanel = styled.div`
  width: 340px;
  flex-shrink: 0;
  border-left: 1px solid ${({ theme }) => theme.modal_border};
  background: ${({ theme }) => theme.prompt_preview_bg || theme.input_surface_bg};
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 900px) {
    display: none;
  }
`;

const PreviewHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.modal_border};
`;

const PreviewTitle = styled.h3`
  margin: 0 0 4px 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${({ theme }) => theme.font_color};
`;

const PreviewSubtitle = styled.p`
  margin: 0;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.text_secondary};
`;

const PreviewContent = styled.div`
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  font-size: 0.82rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.font_color};
  white-space: pre-wrap;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.sidebar_scrollbar};
    border-radius: 3px;
  }
`;

const HighlightedVar = styled.span`
  display: inline;
  padding: 2px 6px;
  margin: 0 2px;
  border-radius: 4px;
  background: ${({ theme }) => theme.prompt_variable_bg};
  color: ${({ theme }) => theme.prompt_variable_text};
  font-weight: 500;
  font-size: 0.78rem;
`;

const PreviewEmpty = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: ${({ theme }) => theme.text_secondary};
  padding: 20px;
  text-align: center;

  & svg {
    font-size: 1.8rem;
    opacity: 0.35;
  }

  & span {
    font-size: 0.82rem;
    line-height: 1.4;
  }
`;

const PromptCard = styled.button<{ $isHovered?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: ${VCARD_H}px;
  overflow: hidden;
  padding: 16px 18px;
  background: ${({ theme, $isHovered }) =>
    $isHovered ? theme.prompt_item_hover : theme.prompt_item_bg};
  border: 1px solid ${({ theme, $isHovered }) =>
    $isHovered ? theme.prompt_accent_color : theme.prompt_item_border};
  border-radius: 16px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, background 0.15s ease;
  text-align: left;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.prompt_item_hover};
    border-color: ${({ theme }) => theme.prompt_accent_color};
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.10);
  }
`;

const PromptCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  width: 100%;
`;

const PromptIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: ${({ theme }) => theme.prompt_category_icon_bg};
  flex-shrink: 0;

  & svg {
    font-size: 1.2rem;
    color: ${({ theme }) => theme.prompt_accent_color};
  }
`;

const PromptTitle = styled.span`
  font-size: 1rem;
  font-weight: 550;
  color: ${({ theme }) => theme.font_color};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PromptDescription = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.text_secondary};
  line-height: 1.6;
  margin-bottom: 12px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PromptCardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: auto;
`;

const PromptVariablesPreview = styled.span`
  font-size: 0.72rem;
  color: ${({ theme }) => theme.prompt_accent_color};
  font-weight: 500;
`;

const PromptActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SmallActionButton = styled.button<{ $danger?: boolean }>`
  background: transparent;
  border: none;
  padding: 4px 8px;
  font-size: 0.72rem;
  font-weight: 500;
  color: ${({ theme, $danger }) =>
    $danger ? theme.text_secondary : theme.prompt_accent_color};
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme, $danger }) =>
      $danger ? 'rgba(231, 76, 60, 0.1)' : theme.hover_overlay_light};
    color: ${({ theme, $danger }) =>
      $danger ? '#e74c3c' : theme.prompt_accent_hover};
  }
`;

const CategoryBadge = styled.span`
  font-size: 0.8rem;
  padding: 4px 11px;
  border-radius: 999px;
  background: ${({ theme }) => theme.prompt_category_icon_bg};
  color: ${({ theme }) => theme.font_color};
  font-weight: 600;
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 0.88rem;
  color: ${({ theme }) => theme.text_secondary};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  padding: 40px;
`;

const EmptyStateIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: ${({ theme }) => theme.prompt_category_icon_bg};
  margin-bottom: 16px;

  & svg {
    font-size: 1.4rem;
    color: ${({ theme }) => theme.text_secondary};
  }
`;

const EmptyStateTitle = styled.h3`
  margin: 0 0 6px 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${({ theme }) => theme.font_color};
`;

const EmptyStateText = styled.p`
  margin: 0 0 20px 0;
  font-size: 0.82rem;
  color: ${({ theme }) => theme.text_secondary};
  max-width: 280px;
`;

const CreateButtonLarge = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 22px;
  border: 1.5px solid ${({ theme }) => theme.prompt_accent_color};
  border-radius: 20px;
  background: transparent;
  color: ${({ theme }) => theme.prompt_accent_color};
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.prompt_accent_color};
    color: #fff;
  }

  & svg {
    font-size: 1.1rem;
  }
`;
